/**
 * useQuotesCatchup
 *
 * Catches up any flights that transitioned while the app was closed:
 *
 *  Any non-terminal status (PENDING / POSITIONING / READY / DELAYED / IN_TRANSIT)
 *    → COMPLETED   aircraft landed at expected destination (FR24 confirms)
 *    → DIVERTED    aircraft landed at a different airport
 *
 * Runs once per browser session (sessionStorage flag) on whichever page
 * mounts the hook first.
 *
 * **Source of flights:** the caller passes the flights that are currently
 * displayed on the screen. The backend's `/flights` endpoint only supports
 * exact-match `flight_status` (not comma lists), so issuing a single query
 * across all non-terminal statuses isn't possible — using the on-screen
 * data avoids the problem and keeps the catchup scoped to what the broker
 * is actually looking at.
 *
 * A flight is "past-due" when its arrival_date has passed (or, if arrival_date
 * is not set, its departure_date has passed) and its DB status is still
 * non-terminal.
 *
 * Route validation: the FR24 summary must match the booked origin AND
 * destination, AND report the flight as ended, before we update. This guards
 * against two failure modes:
 *   (a) marking our flight landed because the registration flew a different
 *       route that day, and
 *   (b) falsely completing a flight that is past its scheduled arrival but
 *       still airborne — such a flight has no `flight_ended` summary yet, so
 *       we must NOT assume it landed.
 * When FR24 has no matching ended record we leave the flight untouched and
 * let the live automation on the detail page (which has real position data)
 * resolve it once the aircraft actually lands.
 *
 * After each flight update the quote status cascade runs via deriveQuoteStatus.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { deriveQuoteStatus } from '@/features/track/utils/flightStatusUtils';
import { isPastReviewThreshold, markNeedsReview, shouldSkipFr24 } from '@/features/track/utils/needsReview';

const SWEPT_KEY = 'gtj_catchup_swept_ids'; // sessionStorage: per-flight dedup

/**
 * @param {Array} flights - Flights currently on screen (e.g. Track list).
 *   Pass `null`/`undefined` when no flight data is available; the hook will skip.
 *
 * Re-fires whenever `flights` changes (e.g. broker navigates to a new date
 * range). Per-flight dedup via sessionStorage prevents checking the same
 * flight twice in one session.
 */
export function useQuotesCatchup(flights = null) {
  const queryClient = useQueryClient();
  const isRunningRef = useRef(false);

  useEffect(() => {
    if (!Array.isArray(flights) || flights.length === 0) return;
    if (isRunningRef.current) return;

    const sweptIds = readSweptIds();
    const TERMINAL = new Set(['COMPLETED', 'DIVERTED', 'CANCELLED']);
    const now = new Date();
    const candidates = flights.filter((f) => {
      const id = f.flight_id || f.id;
      if (!id || sweptIds.has(id)) return false;
      const status = (f.flight_status || '').toUpperCase();
      if (TERMINAL.has(status)) return false;
      const ref = f.arrival_date || f.departure_date;
      return ref && new Date(ref) < now;
    });

    console.log('[QuotesCatchup] hook fired', {
      flightsLoaded: flights.length,
      candidates: candidates.length,
      alreadySwept: sweptIds.size,
    });

    if (!candidates.length) return;

    isRunningRef.current = true;
    runCatchup(queryClient, candidates).finally(() => {
      isRunningRef.current = false;
    });
  }, [queryClient, flights]);
}

function readSweptIds() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SWEPT_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function writeSweptIds(set) {
  try {
    sessionStorage.setItem(SWEPT_KEY, JSON.stringify([...set]));
  } catch { /* sessionStorage full / disabled — non-fatal */ }
}

async function runCatchup(queryClient, pastDue) {
  try {
    console.log('[QuotesCatchup] processing candidates:', pastDue.length, pastDue.map((f) => ({
      id: f.flight_id || f.id,
      status: f.flight_status,
      tail: f.aircraft?.tail_number,
      route: `${f.departure_airport}→${f.arrival_airport}`,
      dep: f.departure_date,
      arr: f.arrival_date,
    })));

    if (!pastDue.length) return;

    // Resolve each via FR24 summary
    const resolved = await Promise.allSettled(
      pastDue.map(async (flight) => {
        // Already determined unresolvable — skip the costly FR24 query entirely.
        if (shouldSkipFr24(flight)) return null;

        // FR24 gave no usable evidence. Flag for human review once we're past
        // the 24h threshold (and stop re-querying); otherwise leave it.
        const silent = () => {
          if (isPastReviewThreshold(flight)) markNeedsReview(flight);
          return null;
        };

        const tailNumber = flight.aircraft?.tail_number;

        // Without a registration we can't verify against FR24 — never guess.
        if (!tailNumber) return silent();

        // Window: ±12 h around the reference date (arrival if set, else departure)
        const refDate = new Date(flight.arrival_date || flight.departure_date);
        const from = new Date(refDate.getTime() - 12 * 60 * 60 * 1000).toISOString();
        const to   = new Date(refDate.getTime() + 12 * 60 * 60 * 1000).toISOString();

        let summaries = [];
        try {
          const summaryData = await apiClient.getFlightSummary(tailNumber, from, to);
          summaries = Array.isArray(summaryData) ? summaryData : (summaryData?.data || []);
        } catch (err) {
          console.warn(`[QuotesCatchup] Summary fetch failed for ${tailNumber}:`, err);
          return null; // transient error — retry next time, don't flag review
        }

        // No FR24 record at all — no evidence the flight landed.
        if (!summaries.length) return silent();

        // Route validation — only treat a summary entry as "our flight" when
        // planned origin AND planned destination match the booked route.
        const expectedDep = (flight.departure_airport || '').toUpperCase();
        const expectedArr = (flight.arrival_airport || '').toUpperCase();

        const routeMatch = summaries.find((s) => {
          const sDep = (s.orig_icao || s.orig_iata || '').toUpperCase();
          const sPlannedArr = (s.dest_icao || s.dest_iata || '').toUpperCase();
          return sDep === expectedDep && sPlannedArr === expectedArr;
        });

        // No record for our route → FR24 is silent for this flight.
        if (!routeMatch) return silent();

        // Matching flight hasn't ended yet (still airborne) → do NOT complete
        // it and do NOT flag review; FR24 has positive evidence it's flying.
        if (!routeMatch.flight_ended) return null;

        // Ended flight on our route. Distinguish COMPLETED from DIVERTED via
        // dest_icao_actual (where it actually landed).
        const sActual = (routeMatch.dest_icao_actual || '').toUpperCase();
        const newStatus = sActual && sActual !== expectedArr ? 'DIVERTED' : 'COMPLETED';
        return { flight, newStatus };
      })
    );

    const updates = resolved
      .filter((r) => r.status === 'fulfilled' && r.value)
      .map((r) => r.value);

    // Mark every candidate as swept (regardless of whether it matched).
    // Prevents re-querying FR24 for the same flights on the next date-range
    // change. Flights still in-flight or with no FR24 record won't be
    // re-checked until the user opens a fresh browser session.
    const sweptIds = readSweptIds();
    for (const f of pastDue) {
      const id = f.flight_id || f.id;
      if (id) sweptIds.add(id);
    }
    writeSweptIds(sweptIds);

    console.log('[QuotesCatchup] updates to apply:', updates.length);
    if (!updates.length) return;

    // Apply updates and cascade to quotes
    await Promise.allSettled(
      updates.map(async ({ flight, newStatus }) => {
        const flightId = flight.flight_id || flight.id;
        try {
          // Optimistic update so the detail-view badge flips immediately,
          // matching the pattern used in useFlightStatusAutomation.
          queryClient.setQueryData(['flightDetails', flightId], (old) => {
            if (!old) return old;
            const apply = (f) =>
              (f?.flight_id === flightId || f?.id === flightId)
                ? { ...f, flight_status: newStatus }
                : f;
            return Array.isArray(old) ? old.map(apply) : apply(old);
          });

          await apiClient.patchFlight(flightId, { flight_status: newStatus });

          const quoteId = flight.quote_id || flight.quote?.quote_id || flight.quote?.id;
          if (quoteId) {
            const newQuoteStatus = await deriveQuoteStatus(quoteId, flightId, newStatus);
            if (newQuoteStatus) {
              await apiClient.updateQuote(quoteId, { status: newQuoteStatus });
            }
          }

          // Invalidate the detail-view query specifically. The bulk-list keys
          // are handled below — but this per-flight key would otherwise be
          // missed because the catchup didn't know it existed.
          queryClient.invalidateQueries({ queryKey: ['flightDetails', flightId] });
        } catch (err) {
          console.error(`[QuotesCatchup] Update failed for flight ${flightId}:`, err);
          // Roll back the optimistic flip so the UI matches reality.
          queryClient.invalidateQueries({ queryKey: ['flightDetails', flightId] });
        }
      })
    );

    queryClient.invalidateQueries({ queryKey: ['quotes'] });
    queryClient.invalidateQueries({ queryKey: ['flights'] });
  } catch (err) {
    console.error('[QuotesCatchup] Catchup failed:', err);
  }
}
